import { t } from 'i18next';

import type { NotificationShowArgs } from 'containers/AlertWrapper/notificationService';
import { LazyRequestPermissionButton } from 'new-components/RequestPermissionButton/LazyRequestPermissionButton';
import {
  isNetworkFetchError,
  getNetworkErrorMessage,
} from 'utils/networkErrorHelpers';

export type CreateErrorAlertArgs = NotificationShowArgs;

/**
 * Creates the arguments for notificationService.show based on error type.
 * Handles special cases like permission errors (H-4000) that require custom UI.
 *
 * @param alertMessage - The message to display in the alert
 * @param alertHeader - Optional header for the alert
 * @param alertOptions - Optional additional alert options
 * @param error - The API error object
 * @returns Arguments array for notificationService.show
 */
export const createErrorAlertArgs = ({
  alertMessage,
  alertHeader,
  alertOptions,
  error,
}: {
  alertMessage: CreateErrorAlertArgs[1];
  alertHeader?: CreateErrorAlertArgs[2];
  alertOptions?: CreateErrorAlertArgs[3];
  error: OP.ApiError;
}): CreateErrorAlertArgs => {
  switch (error.errorData?.errorCode) {
    // Missing permission error
    case 'H-4000':
      return [
        'error',
        alertMessage,
        t('Missing permission'),
        {
          createAlertButtons: (handleAlertClose: () => void) => [
            <LazyRequestPermissionButton
              key="request-permission"
              routeDescription={error.errorData?.values?.routeDescription}
              missingScopeDescriptions={
                error.errorData?.values?.missingScopeDescriptions
              }
              handleAlertClose={handleAlertClose}
            />,
          ],
        },
      ];
    default: {
      const message = isNetworkFetchError(error)
        ? getNetworkErrorMessage()
        : alertMessage;
      return ['error', message, alertHeader, alertOptions];
    }
  }
};
